import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@shopflo/ui/icon";
import { Button } from "@shopflo/ui/button";
import { IconButton } from "@shopflo/ui/icon-button";
import { Input } from "@shopflo/ui/input";
import { Sheet, SheetContent } from "@shopflo/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@shopflo/ui/dialog";
import { At, X, CaretLeft } from "@phosphor-icons/react";
import { formatOtpResendClock, getCheckoutUiRootElement, isMobile } from "lib/utils/helpers";
import { useUserContext } from "lib/contexts/UserProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { sendOtpWithPhoneNumber, verifyOtp, resentOtp, OTPDeliveryType } from "lib/utils/auth";
import { OTPChannel, OTPVerificationDetails } from "lib/types/user";
import { useLocale } from "lib/hooks/useLocale";
import { isValidEmail } from "lib/utils/validations";
import {
  setPrimaryCustomerEmail,
} from "lib/utils/profileEmails";
import OTPInput from "components/auth/OTPInput";
import { CheckboxV2 } from "@shopflo/ui/checkboxv2";
import { bannerErrorToastV2, bannerSuccessToastV2 } from "lib/utils/toasters";

interface EmailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => Promise<void> | void;
  mode: "add" | "edit" | "verify";
  initialEmail?: string;
  isPrimaryEmail?: boolean;
}

const maskEmail = (email: string): string => {
  const [local, domain] = email.split("@");
  if (!local || !domain) return email;
  if (local.length <= 4) {
    return `${local[0]}***@${domain}`;
  }
  const visible = local.slice(0, 3);
  const lastTwo = local.slice(-2);
  return `${visible}***${lastTwo}@${domain}`;
};

const EmailDialog: React.FC<EmailDialogProps> = ({ isOpen, onClose, onSuccess, mode, initialEmail = "", isPrimaryEmail = false }) => {
  const { t } = useLocale();
  const {
    state: { user },
  } = useUserContext();
  const {
    state: { merchant, accountId },
  } = useMerchantContext();


  // Screen state
  const [currentScreen, setCurrentScreen] = useState<"email" | "otp">("email");

  // Email form state
  const [email, setEmail] = useState(initialEmail);
  const [useAsPreferred, setUseAsPreferred] = useState(false);

  // OTP state
  const [otp, setOtp] = useState("");
  const [contextId, setContextId] = useState("");
  const [counter, setCounter] = useState(-1);
  const [counterExpired, setCounterExpired] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setCurrentScreen("email");
      setOtp("");
      setContextId("");
      setCounter(-1);
      setCounterExpired(false);
      setIsLoading(false);
      setError(false);
      setErrorMessage("");
      return;
    }
    setCurrentScreen("email");
    setEmail(initialEmail);
    setUseAsPreferred(mode === "edit" ? isPrimaryEmail : false);
    setOtp("");
    setContextId("");
    setCounter(-1);
    setCounterExpired(false);
    setIsLoading(false);
    setError(false);
    setErrorMessage("");
  }, [isOpen, initialEmail, mode, isPrimaryEmail]);

  // OTP countdown timer
  useEffect(() => {
    if (counter === 0) setCounterExpired(true);
    if (counter <= 0) return;
    const timer = setTimeout(() => setCounter(counter - 1), 1000);
    return () => clearTimeout(timer);
  }, [counter]);

  const handleSendOtp = async () => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setError(true);
      setErrorMessage(t("email_required"));
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setError(true);
      setErrorMessage(t("valid_email"));
      return;
    }

    try {
      setIsLoading(true);
      setError(false);
      setErrorMessage("");

      const resolvedMerchantId = merchant?.merchantId?.trim() || accountId?.trim() || "";

      const verificationDetails: OTPVerificationDetails = {
        identifier: trimmedEmail,
        userId: user?.uid,
        forceVerification: true,
        merchantId: resolvedMerchantId,
      };

      const contextMetadata: OTPDeliveryType = {
        context: "AUTH",
        requestContext: "LOGIN",
      };

      const result = await sendOtpWithPhoneNumber(
        verificationDetails,
        contextMetadata,
        "",
        merchant?.captchaProvider ?? "TURNSTILE",
      );

      if (result?.context_id) {
        setContextId(result.context_id);
        setCounter(30);
        setCounterExpired(false);
        setCurrentScreen("otp");
      } else {
        setError(true);
        setErrorMessage(t("shopify_login.send_otp_failed"));
      }
    } catch {
      setError(true);
      setErrorMessage(t("shopify_login.send_otp_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = useCallback(async () => {
    if (!otp || otp.length !== 4) return;

    setIsLoading(true);
    setError(false);
    setErrorMessage("");

    try {
      await verifyOtp(contextId, otp, true);
    } catch {
      setError(true);
      setErrorMessage(t("shopify_login.incorrect_otp"));
      setIsLoading(false);
      return;
    }

    try {
      if (mode === "verify" || useAsPreferred) {
        await setPrimaryCustomerEmail(email.trim());
      }

      await onSuccess?.();

      if (mode === "add") {
        bannerSuccessToastV2(
          useAsPreferred ? t("profile_email_toast_added_and_primary") : t("profile_email_toast_added"),
        );
      } else if (mode === "edit") {
        bannerSuccessToastV2(
          useAsPreferred ? t("profile_email_toast_updated_and_primary") : t("profile_email_toast_updated"),
        );
      } else {
        bannerSuccessToastV2(t("profile_email_toast_verified_default"));
      }

      onClose();
    } catch {
      setError(true);
      setErrorMessage(t("shopify_login.update_email_failed"));
    } finally {
      setIsLoading(false);
    }
  }, [otp, contextId, mode, useAsPreferred, email, t, onSuccess, onClose]);

  const handleResendOtp = async () => {
    try {
      setIsLoading(true);
      setOtp("");
      setError(false);
      await resentOtp(contextId, "EMAIL" as OTPChannel);
      setCounter(30);
      setCounterExpired(false);
    } catch {
      bannerErrorToastV2(t("shopify_login.send_otp_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || currentScreen !== "otp" || !contextId || otp.length !== 4) return;
    void handleVerifyOtp();
  }, [isOpen, currentScreen, contextId, otp, handleVerifyOtp]);

  const handleBack = () => {
    setCurrentScreen("email");
    setOtp("");
    setError(false);
    setErrorMessage("");
  };

  const dialogTitle =
    currentScreen === "otp"
      ? t("shopify_login.verify_email")
      : mode === "add"
        ? t("profile_email_dialog_title_new")
        : mode === "edit"
          ? t("profile_email_dialog_title_edit")
          : t("shopify_login.verify_email");

  const isEmailModified = email.trim() !== initialEmail.trim();
  const isToggleChanged = useAsPreferred !== isPrimaryEmail;
  const isButtonDisabled =
    !email.trim() || isLoading || (mode === "edit" && !isEmailModified && !isToggleChanged);
  const showToggle = !(mode === "edit" && isPrimaryEmail);

  const header = (
    <div className="flex items-center justify-between px-5 pt-4">
      <div className="flex items-center gap-3">
        {currentScreen === "otp" ? (
          <div className="flex h-9 w-9 items-center justify-center !rounded-lg border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(10,13,18,0.05)]" onClick={handleBack}>
            <Icon IconComponent={CaretLeft} size="xs" className="text-zinc-500" />
          </div>
        ) : (
          <div className="flex h-10 w-10 items-center justify-center !rounded-xl bg-zinc-100">
            <Icon IconComponent={At} size="md" className="text-zinc-500" iconProps={{ weight: "duotone" }} />
          </div>
        )}
        <DialogTitle className="text-text-md-semibold text-zinc-800 !text-base !font-semibold !leading-none !tracking-normal">
          {dialogTitle}
        </DialogTitle>
      </div>
      <DialogClose asChild>
        <IconButton IconComponent={X} size="xxs" variant="ghost" className="!p-[7px]" />
      </DialogClose>
    </div>
  );

  const primaryButtonClass =
    "w-full rounded-lg shadow-[0_1px_0px_0px_rgba(0,0,0,0.15),0_3px_6px_-3px_rgba(0,0,0,0.2),0_4px_8px_0px_rgba(0,0,0,0.05),inset_0_2px_0px_0px_rgba(255,255,255,0.3),inset_0_0.5px_0px_0px_rgba(0,0,0,0.25),inset_0_-1px_0px_0px_rgba(0,0,0,0.25)]";
  const cancelButtonClass =
    "w-full rounded-lg border-zinc-200 shadow-[0_0.5px_0px_0px_rgba(0,0,0,0.15),0_2px_6px_-1px_rgba(0,0,0,0.1)]";

  const footer = (
    <div className="flex flex-col gap-2 px-5 pb-4">
      {currentScreen === "email" ? (
        <>
          <Button
            variant="primary"
            size="md"
            onClick={handleSendOtp}
            disabled={isButtonDisabled}
            className={primaryButtonClass}>
            {isLoading ? t("profile_send_otp_loading") : mode === "verify" ? t("shopify_login.verify_email") : t("profile_email_next_verify")}
          </Button>
          <Button variant="outline" size="md" onClick={onClose} className={cancelButtonClass}>
            {t("cancel")}
          </Button>
        </>
      ) : (
        <>
          <Button
            variant="primary"
            size="md"
            onClick={handleVerifyOtp}
            disabled={!otp || otp.length !== 4 || isLoading}
            className={primaryButtonClass}>
            {isLoading ? t("profile_verify_otp_loading") : t("profile_email_verify_save")}
          </Button>
          <Button variant="outline" size="md" onClick={onClose} className={cancelButtonClass}>
            {t("cancel")}
          </Button>
        </>
      )}
    </div>
  );

  const content = (
    <>
      <div className="flex flex-col gap-4 p-5">
        {currentScreen === "email" ? (
          /* Screen 1: Email Form */
          <div className="flex flex-col gap-4">
            <Input
              variant="outline"
              dimension="lg"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) {
                  setError(false);
                  setErrorMessage("");
                }
              }}
              placeholder={t("shopify_login.email_placeholder")}
              error={error}
              readOnly={mode === "verify"}
              startAdornment={<Icon IconComponent={At} size="sm" className="text-zinc-400" />}
              className="rounded-2xl"
            />
            {error && errorMessage && (
              <p className="text-text-xs-regular text-red-600">{errorMessage}</p>
            )}

            {showToggle && (
              <div className="flex items-start justify-between border-t border-zinc-100 pt-4 pb-2 px-[6px]">
                <div className="flex flex-col gap-0.5">
                  <span className="text-text-sm-medium text-zinc-700">{t("profile_email_preferred_toggle")}</span>
                  <span className="text-text-xs-regular text-zinc-500">{t("profile_email_preferred_toggle_hint")}</span>
                </div>
                <CheckboxV2
                  checked={useAsPreferred}
                  type="switch"
                  onCheckedChange={(checked) => setUseAsPreferred(checked === true)}
                />
              </div>
            )}
          </div>
        ) : (
          /* Screen 2: OTP Verification */
          <div className="flex flex-col gap-4">
            <p className="text-text-sm-medium text-zinc-500">
              {t("profile_email_otp_sent_with_email", { email: maskEmail(email) })}
            </p>

            <div className="flex flex-col gap-4">
              <OTPInput
                OTPLength={4}
                disabled={isLoading}
                value={otp}
                onChange={(value) => {
                  setOtp(value);
                  if (error) {
                    setError(false);
                    setErrorMessage("");
                  }
                }}
                hasError={error}
                autoFocus={true}
                inputType="tel"
                disableTheme={true}
                inputClassName="!h-11 !w-full !rounded-[10px] !border-transparent !bg-white !px-[10px] !text-base !font-medium !text-zinc-400 !shadow-[0_0_0_1px_rgba(106,114,130,0.24),0_1px_3px_0_rgba(26,26,26,0.08)] focus:!w-full focus:!border-transparent focus:!text-zinc-500 focus:!shadow-[0_0_0_1.5px_#4A5565,0_1.5px_4px_0_rgba(9,9,11,0.12)] focus:!ring-0"
              />

              {error && <p className="text-text-xs-regular text-red-600 text-center">{errorMessage}</p>}

              <div className="flex items-center gap-1">
                <span className="text-text-sm-medium text-[#191919]/70">{t("shopify_login.not_received")}</span>
                {counterExpired ? (
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    className="text-[#3641F5] text-text-sm-semibold cursor-pointer hover:opacity-80 transition-opacity"
                    disabled={isLoading}>
                    {t("resend_otp")}
                  </button>
                ) : counter > 0 ? (
                  <span className="text-text-sm-medium text-zinc-400">
                    {t("profile_resend_otp_in", {
                      time: formatOtpResendClock(counter),
                    })}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
      {footer}
    </>
  );
  if (isMobile()) {
    return (
      <Sheet open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
        <SheetContent
          side="bottom"
          className="h-auto rounded-t-2xl !p-0"
          overlayClassName="!bg-black/40">
          {header}
          {content}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        withinContainer
        container={getCheckoutUiRootElement() ?? undefined}
        className="pointer-events-auto overflow-hidden !rounded-2xl border-0 bg-white !p-0 !gap-0 shadow-[0_4px_6px_0px_rgba(0,0,0,0.03),0_12px_18px_0px_rgba(0,0,0,0.05),0_54px_80px_-5px_rgba(0,0,0,0.15),0_0px_0px_1.2px_rgba(0,0,0,0.04)]"
        style={{ width: "360px", maxWidth: "360px" }}
      >
        <DialogDescription className="sr-only">
          {currentScreen === "email" ? t("profile_email_a11y_enter_email") : t("profile_email_a11y_enter_otp")}
        </DialogDescription>
        {header}
        {content}
      </DialogContent>
    </Dialog>
  );
};

export default EmailDialog;
